import { AxiosError } from "axios";
import { captureException } from "@/shared/clients/sentry.client";
import type { NotificationService } from "@/shared/services/notification.service";
import type {
  ApiError,
  ApiErrorResponse,
  ErrorConfig,
  ErrorContext,
  ToastErrorOptions,
} from "@/shared/types/error.types";

export type ErrorHandlerService = {
  handleError: (error: unknown, config?: ErrorConfig, context?: ErrorContext) => void
  extractApiError: (error: unknown) => ApiError
};

const DEFAULT_ERROR_TITLE = "Erreur";
const GENERIC_ERROR_DESCRIPTION = "Une erreur est survenue";
const TIMEOUT_ERROR: ApiError = {
  code: "timeout",
  title: DEFAULT_ERROR_TITLE,
  description: "Le serveur met trop de temps à répondre. Réessayez dans quelques instants.",
};
const NETWORK_ERROR: ApiError = {
  code: "network_error",
  title: DEFAULT_ERROR_TITLE,
  description: "Impossible de contacter le serveur. Vérifiez votre connexion et réessayez.",
};
const NO_RESPONSE_ERRORS: Partial<Record<string, ApiError>> = {
  [AxiosError.ECONNABORTED]: TIMEOUT_ERROR,
  [AxiosError.ETIMEDOUT]: TIMEOUT_ERROR,
  [AxiosError.ERR_NETWORK]: NETWORK_ERROR,
};
const NO_RESPONSE_FALLBACK: ApiError = {
  code: "unknown",
  title: DEFAULT_ERROR_TITLE,
  description: GENERIC_ERROR_DESCRIPTION,
};

// --------- Internal helpers to reduce complexity ---------
const isAxiosError = (error: unknown): error is AxiosError => error instanceof AxiosError;

const toApiErrorFromArray = (messages: string[], status?: number): ApiError => ({
  code: status?.toString() || "422",
  title: DEFAULT_ERROR_TITLE,
  description: messages.join("\n"),
});

const toClientApiError = (error: Error): ApiError => ({
  code: "client_error",
  title: DEFAULT_ERROR_TITLE,
  description: error.message,
});

const toUnknownApiError = (): ApiError => ({
  code: "unknown",
  title: DEFAULT_ERROR_TITLE,
  description: "Une erreur inconnue est survenue",
});

const toStatusApiError = (description: string, status?: number): ApiError => ({
  code: status?.toString() || "unknown",
  title: DEFAULT_ERROR_TITLE,
  description,
});

// Ordered by preference: yeati-api sends `detail`, `message` covers other error shapes.
const ERROR_ITEM_TEXT_FIELDS = ["detail", "message"] as const;

const readStringField = (item: object, field: string): string => {
  const value: unknown = Reflect.get(item, field);
  return typeof value === "string" ? value : "";
};

// The API lists errors as plain strings or as `{ detail, code? }` objects; anything else is not user-facing text.
const toErrorMessage = (item: unknown): string => {
  if (typeof item === "string") return item;
  if (typeof item !== "object" || item === null) return "";
  return ERROR_ITEM_TEXT_FIELDS.map((field) => readStringField(item, field)).find(Boolean) ?? "";
};

const normalizeErrorsArray = (errors: unknown): string[] => {
  if (!Array.isArray(errors)) return [];
  return errors.map(toErrorMessage).filter(Boolean);
};

const getApiErrorFromPayload = (
  data: unknown,
  status?: number,
): ApiError | null => {
  if (Array.isArray(data)) return toApiErrorFromArray(data, status);

  const withError = (data as ApiErrorResponse)?.error;
  if (typeof withError === "string") return toStatusApiError(withError, status);
  if (withError) return withError;

  const normalized = normalizeErrorsArray((data as { errors?: unknown })?.errors);
  if (normalized.length > 0) return toApiErrorFromArray(normalized, status);

  const message = (data as { message?: string })?.message;
  if (message) return toStatusApiError(message, status);

  return null;
};

const getConstructorName = (value: unknown): string | undefined => {
  if (value === null || value === undefined) return undefined;
  const constructor: unknown = Object.getPrototypeOf(Object(value))?.constructor;
  return typeof constructor === "function" ? constructor.name : undefined;
};

// Bots inject a wrapper over the global Error that breaks `instanceof` for every error on the page.
const isGlobalErrorPatched = (): boolean => !(TypeError.prototype instanceof Error);

// Shape only, never the value itself: a rejected payload can carry personal data or a token.
const describeRejectedValue = (error: unknown) => ({
  type: typeof error,
  constructorName: getConstructorName(error),
  isErrorPatched: isGlobalErrorPatched(),
});

const toApiErrorFromAxios = (error: AxiosError): ApiError => {
  const payload = error.response?.data;
  const derived = getApiErrorFromPayload(payload, error.response?.status);
  if (derived) return derived;

  // Without a response, axios only has technical messages ("timeout of 30000ms exceeded") unfit for users.
  if (!error.response) {
    return NO_RESPONSE_ERRORS[error.code ?? ""] ?? NO_RESPONSE_FALLBACK;
  }

  return toStatusApiError(error.message || GENERIC_ERROR_DESCRIPTION, error.response.status);
};

// --------- Factory ---------
export const createErrorHandlerService = (
  notificationService: NotificationService,
): ErrorHandlerService => {
  const extractApiError = (error: unknown): ApiError => {
    if (isAxiosError(error)) return toApiErrorFromAxios(error);
    if (error instanceof Error) return toClientApiError(error);
    return toUnknownApiError();
  };

  const handleError = (error: unknown, config: ErrorConfig = {}, context: ErrorContext = {}) => {
    const {
      showToast = true,
      title,
      description,
      silent = false,
      customHandler,
    } = config;

    const apiError = extractApiError(error);

    if (!silent) {
      const isError = error instanceof Error;

      captureException(isError ? error : new Error(apiError.description), {
        tags: { source: "error-handler-service" },
        extra: {
          apiError,
          config,
          ...context,
          ...(isError ? {} : { rejectedValue: describeRejectedValue(error) }),
        },
      });
    }

    if (customHandler) {
      customHandler(apiError);
      return;
    }

    if (silent) {
      return;
    }

    if (showToast) {
      const GENERIC_MUTATION_DESCRIPTION = "Une erreur est survenue lors de l'opération";
      const finalDescription = description === GENERIC_MUTATION_DESCRIPTION
        ? apiError.description
        : (description || apiError.description);
      const toastOptions: ToastErrorOptions = {
        title: title || apiError.title,
        description: finalDescription,
      };

      notificationService.showError(toastOptions);
    }
  };

  return {
    handleError,
    extractApiError,
  };
};
